import React, { useState } from 'react';
import { Maximize2 } from 'lucide-react';
import {
  cn,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  linkifyText,
  ClampedCellContent,
} from '@strivelabs/ui-core';

/** A cell value with optional text color. */
export type ColoredCellValue = { value: string | number | null; color?: string };

/** A single cell can be a plain primitive or a colored cell object. */
export type CellValue = string | number | null | ColoredCellValue;

/** Type guard: checks if a cell value is a colored cell object. */
function isColoredCell(val: CellValue): val is ColoredCellValue {
  return typeof val === 'object' && val !== null && 'value' in val;
}

/** Extract the raw display value from a cell. */
function extractCellValue(val: CellValue): string | number | null {
  return isColoredCell(val) ? val.value : val;
}

/** Extract the optional color from a cell. */
function extractCellColor(val: CellValue): string | undefined {
  return isColoredCell(val) ? val.color : undefined;
}

export interface TableWrapperProps {
  // Required
  headers: string[];
  rows: CellValue[][];

  // Optional
  title?: string;
  description?: string;
  variant?: "default" | "minimal" | "spacious";
  enableExport?: boolean;
  exportFilename?: string;
  wrapperClassName?: string;  // Styling for the outer container
  tableClassName?: string;     // Styling for the inner table
  expandable?: boolean;
  expandedMaxHeight?: string;
  /** Pin the first column during horizontal scroll. Default: true */
  pinFirstColumn?: boolean;
  /** Max width for cell content. When set, content wraps and clamps at `maxLines` with "View more" toggle. Omit for unconstrained cells. */
  cellMaxWidth?: string;
  /** Maximum visible lines before clamping. Default: 3 */
  maxLines?: number;
}

// ============ Cell content helpers ============

function CellContent({ value, color }: { value: string | number | null; color?: string }) {
  if (value === null) return <>{'—'}</>;
  const content = linkifyText(String(value));
  if (color) return <span style={{ color }}>{content}</span>;
  return <>{content}</>;
}

/**
 * TableWrapper - Wrapper component for tables with expand-to-dialog functionality
 *
 * Similar to ChartWrapper, this component wraps a table and provides:
 * - Expand button to view table in fullscreen dialog
 * - Optional title and description
 * - Export functionality (CSV, Markdown)
 * - Support for different visual variants
 * - First-column pinning for wide tables (enabled by default)
 * - Per-cell text wrapping with line-clamp and "View more" toggle
 *
 * For tables with many columns, the expand button helps users view data comfortably.
 */
export const TableWrapper: React.FC<TableWrapperProps> = ({
  headers,
  rows,
  title,
  description,
  variant = 'default',
  enableExport = true,
  exportFilename = 'table-data',
  wrapperClassName,
  tableClassName,
  expandable = true,
  expandedMaxHeight = '70vh',
  pinFirstColumn = true,
  cellMaxWidth,
  maxLines = 3,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Check if table is empty
  const isEmpty = rows.length === 0;

  const renderTable = () => (
    <Table
      variant={variant}
      enableExport={enableExport}  // Show export in both normal and expanded views
      exportFilename={exportFilename}
      className={tableClassName}
    >
      <TableHeader variant={variant}>
        <TableRow variant={variant}>
          {headers.map((header, idx) => (
            <TableHead
              key={idx}
              variant={variant}
              pinned={pinFirstColumn && idx === 0 ? "left" : undefined}
            >
              {header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {isEmpty ? (
          <TableRow variant={variant}>
            <TableCell variant={variant} colSpan={headers.length} className="text-center text-muted-foreground py-8">
              No data available
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row, rowIdx) => (
            <TableRow key={rowIdx} variant={variant}>
              {row.map((cell, cellIdx) => {
                const rawValue = extractCellValue(cell);
                const color = extractCellColor(cell);
                return (
                  <TableCell
                    key={cellIdx}
                    variant={variant}
                    pinned={pinFirstColumn && cellIdx === 0 ? "left" : undefined}
                  >
                    {cellMaxWidth ? (
                      <div style={{ maxWidth: cellMaxWidth }}>
                        <ClampedCellContent maxLines={maxLines}>
                          <CellContent value={rawValue} color={color} />
                        </ClampedCellContent>
                      </div>
                    ) : (
                      <CellContent value={rawValue} color={color} />
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  return (
    <>
      {/* Main table view - styled like ChartWrapper with outline */}
      <div
        className={cn(
          'flex flex-col gap-4 bg-custom-bg-primary border border-custom-border rounded-lg p-4 relative',
          wrapperClassName
        )}
      >
        {/* Expand Button - Top Right (matching ChartWrapper positioning) */}
        {expandable && !isEmpty && (
          <Button
            variant="ghost"
            size="iconSm"
            onClick={() => setIsDialogOpen(true)}
            className="absolute top-3 right-3 z-10 bg-custom-card-bg/60 border border-custom-border shadow-sm hover:bg-custom-card-bg/80 cursor-pointer"
            aria-label="Expand table"
          >
            <Maximize2 className="size-3.5 text-muted-foreground" />
          </Button>
        )}

        {/* Header - matching ChartWrapper style */}
        {(title || description) && (
          <div className="flex flex-col gap-1">
            {title && (
              <h3 className="text-sm text-custom-text-secondary font-semibold leading-tight tracking-tight pr-8">
                {title}
              </h3>
            )}
            {description && (
              <p className="text-sm text-muted-foreground pr-8">{description}</p>
            )}
          </div>
        )}

        {/* Table Content — p-px prevents overflow-auto from clipping the table's box-shadow */}
        <div className="w-full max-h-[400px] overflow-auto p-px">
          {renderTable()}
        </div>
      </div>

      {/* Expanded dialog view */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-7xl w-[95vw] max-h-[90vh] overflow-auto">
          <DialogHeader>
            {title && <DialogTitle>{title}</DialogTitle>}
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          <div className="mt-4" style={{ maxHeight: expandedMaxHeight }}>
            {renderTable()}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
